import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInAnonymously, signOut } from "firebase/auth";
import { auth } from "../firebase";
import QRCode from 'qrcode';

const NAVY = "#1A3B6E";
const MID = "#2C5FA8";
const LIGHT = "#E8F2FB";

const CLAIM_WINDOW_MS = 30 * 60 * 1000;

function relativeTime(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
}

function formatCycleDate(value) {
  if (!value) return "";
  const d = new Date(value + (value.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const S = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#F7FAFD",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    backgroundColor: NAVY,
    padding: "16px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "sticky",
    top: 0,
    zIndex: 50,
  },
  headerTitle: { color: "#FFF", fontSize: 18, fontWeight: 700, margin: 0 },
  headerSub: {
    color: "rgba(255,255,255,0.6)",
    fontSize: 12,
    margin: "2px 0 0",
  },
  endBtn: {
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.3)",
    color: "#FFF",
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  content: { padding: "16px 16px 160px" },
  card: {
    backgroundColor: "#FFF",
    borderRadius: 14,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "18px 16px",
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: NAVY,
    margin: "0 0 12px",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    fontSize: 16,
    border: "1.5px solid #e2e8f0",
    borderRadius: 10,
    boxSizing: "border-box",
    outline: "none",
    fontFamily: "inherit",
    marginBottom: 10,
    WebkitAppearance: "none",
  },
  btnPrimary: {
    width: "100%",
    padding: "15px",
    fontSize: 15,
    fontWeight: 700,
    color: "#FFF",
    backgroundColor: NAVY,
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    minHeight: 52,
    WebkitAppearance: "none",
  },
  btnDisabled: { backgroundColor: "#94a3b8", cursor: "not-allowed" },
  loginContainer: {
    minHeight: "100vh",
    backgroundColor: "#F7FAFD",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  loginCard: {
    backgroundColor: "#FFF",
    borderRadius: 16,
    padding: "32px 24px",
    maxWidth: 400,
    width: "100%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    textAlign: "center",
  },
};

export default function PackingPortal() {
  const [step, setStep] = useState("login");
  const [volunteerName, setVolunteerName] = useState("");
  const [sessionCode, setSessionCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [cycleInfo, setCycleInfo] = useState(null);
  const [queue, setQueue] = useState(null);
  const [claimedOrder, setClaimedOrder] = useState(null);
  const [view, setView] = useState("queue"); // queue | checklist | result
  const [toast, setToast] = useState(null);
  const [minutesRemaining, setMinutesRemaining] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [packSubmitting, setPackSubmitting] = useState(false);
  const [packResult, setPackResult] = useState(null); // null | 'packed' | 'flagged'
  const [bagCount, setBagCount] = useState(1);
  const [showBagInput, setShowBagInput] = useState(false);
  const navigate = useNavigate();

  // --- Login ---
  const handleStart = async () => {
    if (!volunteerName.trim() || !sessionCode.trim() || loading) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetch("/api/volunteer/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: volunteerName.trim(), sessionCode }),
      });
      if (res.status === 403) {
        setErrorMsg("Invalid session code — check with your coordinator.");
        return;
      }
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setCycleInfo(data);
      await signInAnonymously(auth);
      setStep("portal");
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Queue fetch ---
  const fetchQueue = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("/api/volunteer/queue", {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-session-code": sessionCode,
          "x-volunteer-name": volunteerName,
        },
      });
      if (!res.ok) return;
      const data = await res.json();
      setQueue(data);
    } catch {
      // silent — retry on next interval
    }
  }, [sessionCode, volunteerName]);

  // Auto-refresh queue every 15 seconds when in portal
  useEffect(() => {
    if (step !== "portal") return;
    fetchQueue();
    const id = setInterval(fetchQueue, 15000);
    return () => clearInterval(id);
  }, [step, fetchQueue]);

  // Toast auto-dismiss after 5 seconds
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(id);
  }, [toast]);

  // Claim countdown ticker
  useEffect(() => {
    if (!claimedOrder?.claimedAt) {
      setMinutesRemaining(null);
      return;
    }
    const tick = () => {
      const deadline =
        new Date(claimedOrder.claimedAt).getTime() + CLAIM_WINDOW_MS;
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 60000));
      setMinutesRemaining(remaining);
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [claimedOrder]);

  // --- Load checklist from picklist endpoint ---
  const loadChecklist = useCallback(
    async (orderId, tokenId) => {
      if (!auth.currentUser || !cycleInfo) return;
      setChecklistLoading(true);
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(
          `/api/orders/picklist?cycleId=${cycleInfo.cycle?.cycleId || cycleInfo.cycleId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-session-code": sessionCode,
              "x-volunteer-name": volunteerName,
            },
          },
        );
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const data = await res.json();
        const bags = data.bags || [];
        const bag = bags.find((b) => b.tokenId === tokenId);
        if (bag) {
          setChecklistItems(
            (bag.items || []).map((item) => ({
              itemId: item.item,
              name: item.item,
              qty: item.qty,
              checked: false,
              flagged: false,
              flagReason: "",
              flagNotes: "",
            })),
          );
        } else {
          setChecklistItems([]);
        }
      } catch (err) {
        setToast(err.message);
      } finally {
        setChecklistLoading(false);
      }
    },
    [cycleInfo, sessionCode, volunteerName],
  );

  // --- Claim ---
  const handleClaim = async (orderId) => {
    if (!auth.currentUser) return;
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`/api/orders/${orderId}/claim`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "x-session-code": sessionCode,
          "x-volunteer-name": volunteerName,
        },
      });
      if (res.status === 409) {
        const data = await res.json();
        setToast(data.message || "Order already claimed — try another.");
        fetchQueue();
        return;
      }
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const order = data.order || data;
      setClaimedOrder(order);
      setView("checklist");
      loadChecklist(orderId, order.tokenId);
    } catch (err) {
      setToast(err.message);
    }
  };

  // --- Release ---
  const handleRelease = async () => {
    if (!claimedOrder || !auth.currentUser) return;
    try {
      const token = await auth.currentUser.getIdToken();
      await fetch(`/api/orders/${claimedOrder.orderId}/release`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "x-session-code": sessionCode,
          "x-volunteer-name": volunteerName,
        },
      });
    } catch {
      // best-effort
    }
    setClaimedOrder(null);
    setView("queue");
    fetchQueue();
  };

  // --- Toggle item checked ---
  const toggleItemChecked = (index) => {
    setChecklistItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const nowChecked = !item.checked;
        return {
          ...item,
          checked: nowChecked,
          flagged: nowChecked ? false : item.flagged,
        };
      }),
    );
  };

  // --- Toggle item flagged ---
  const toggleItemFlag = (index) => {
    setChecklistItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const nowFlagged = !item.flagged;
        return {
          ...item,
          flagged: nowFlagged,
          checked: nowFlagged ? false : item.checked,
        };
      }),
    );
  };

  // --- Confirm pack ---
  const handleConfirmPack = async () => {
    const checked = checklistItems.filter((i) => i.checked);
    const flagged = checklistItems.filter((i) => i.flagged);
    if (checked.length === 0 && flagged.length === 0) {
      setToast("Check off at least one item or flag issues before confirming");
      return;
    }
    setPackSubmitting(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const checkedItems = checked.map((i) => i.name);
      const flaggedItems = flagged.map((i) => ({
        itemId: i.name,
        reason: i.flagReason || "missing_from_shelf",
        notes: i.flagNotes || "",
      }));
      const res = await fetch(`/api/orders/${claimedOrder.orderId}/pack`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "x-session-code": sessionCode,
          "x-volunteer-name": volunteerName,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ checkedItems, flaggedItems }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setPackResult("packed");
      setView("result");
    } catch (err) {
      setToast(err.message);
    } finally {
      setPackSubmitting(false);
    }
  };

  // --- Flag entire order ---
  const handleFlagOrder = async () => {
    setPackSubmitting(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const items = checklistItems
        .filter((i) => i.flagged)
        .map((i) => ({
          itemId: i.name,
          reason: i.flagReason || "missing_from_shelf",
          notes: i.flagNotes || "",
        }));
      const res = await fetch(`/api/orders/${claimedOrder.orderId}/flag`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "x-session-code": sessionCode,
          "x-volunteer-name": volunteerName,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          reason: "missing_from_shelf",
          items,
          notes: "",
        }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setPackResult("flagged");
      setView("result");
    } catch (err) {
      setToast(err.message);
    } finally {
      setPackSubmitting(false);
    }
  };

  // --- Print bag label ---
  const printBagLabel = async (count) => {
    const qrDataUrl = await QRCode.toDataURL(
      `https://riverwoodraiderdepot.org/order/${claimedOrder.tokenId}`,
      { width: 120, margin: 1 }
    );



    const pickupStr = cycleDate || cycleInfo?.cycleId || '—';
    const cycleId = cycleInfo?.cycle?.cycleId || cycleInfo?.cycleId || '—';
    const todayStr = new Date().toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });

    const itemRows = checklistItems
      .map((item) => `<tr>
        <td style="padding:3px 8px;border:1px solid #e2e8f0;font-size:11px;${item.flagged ? 'text-decoration:line-through;color:#999;' : ''}">${item.name}</td>
        <td style="padding:3px 8px;border:1px solid #e2e8f0;font-size:11px;text-align:center;font-weight:600;">${item.qty}</td>
      </tr>`)
      .join('');

    const renderSheet = (bagNum) => `
<div style="page-break-after:${bagNum < count ? 'always' : 'auto'};padding:0.25in;font-family:Arial,sans-serif;max-width:8.5in;box-sizing:border-box;">

  <div style="background:#EFF6FF;border:2px solid #1E3A5F;border-radius:10px;display:flex;align-items:center;gap:16px;padding:16px 20px;margin-bottom:14px;">
    <img src="${qrDataUrl}" alt="QR Code" width="120" height="120" style="border:1px solid #cbd5e1;border-radius:4px;flex-shrink:0;" />
    <div style="flex:1;text-align:center;">
      <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">Your Order Token</div>
      <div style="font-size:48px;font-weight:700;color:#1A3B6E;letter-spacing:2px;line-height:1.1;">${claimedOrder.tokenId}</div>
      <div style="font-size:24px;font-weight:700;color:#374151;margin-top:6px;letter-spacing:1px;">Bag ${bagNum} of ${count}</div>
    </div>
  </div>

  <table style="width:100%;border-collapse:collapse;margin-bottom:14px;font-size:12px;">
    <tbody><tr>
      <td style="padding:4px 8px;color:#6b7280;width:25%;">Cycle</td>
      <td style="padding:4px 8px;color:#6b7280;width:25%;">Scheduled pickup</td>
      <td style="padding:4px 8px;color:#6b7280;width:25%;">Packed</td>
      <td style="padding:4px 8px;color:#6b7280;width:25%;">Packed by</td>
    </tr><tr>
      <td style="padding:4px 8px;font-weight:600;color:#1e293b;">${cycleId}</td>
      <td style="padding:4px 8px;font-weight:600;color:#1e293b;">${pickupStr}</td>
      <td style="padding:4px 8px;font-weight:600;color:#1e293b;">${todayStr}</td>
      <td style="padding:4px 8px;font-weight:600;color:#1e293b;">${volunteerName}</td>
    </tr></tbody>
  </table>

  <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
    <thead><tr>
      <th style="background:#F0FDF4;padding:3px 8px;border:1px solid #e2e8f0;text-align:left;font-size:11px;color:#374151;">Item</th>
      <th style="background:#F0FDF4;padding:3px 8px;border:1px solid #e2e8f0;text-align:center;font-size:11px;color:#374151;width:50px;">Qty</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
  </table>

  <div style="background:#FEF3C7;border-radius:6px;padding:8px 12px;margin-bottom:10px;">
    <div style="font-weight:700;color:#92400e;font-size:12px;margin-bottom:2px;">Please return your empty bags!</div>
    <div style="font-size:11px;color:#78350f;line-height:1.4;">When you pick up your next order, please bring back any empty shopping bags. Returned bags help us serve more families. Thank you!</div>
  </div>

  <div style="font-size:11px;color:#374151;line-height:1.6;">
    <span style="margin-right:16px;"><strong>Register:</strong> Visit riverwoodraiderdepot.org and scan your QR card.</span>
    <span><strong>Feedback:</strong> riverwoodraiderdepot.org/feedback</span>
  </div>

</div>`;

    const sheets = Array.from({ length: count }, (_, i) => renderSheet(i + 1)).join('');
    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Pickup Sheet — ${claimedOrder.tokenId}</title>
<style>
  @page { size: letter portrait; margin: 0.15in; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 0; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>${sheets}</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
    setShowBagInput(false);
    setBagCount(1);
  };

  // --- Pack complete / back to queue ---
  const handlePackComplete = () => {
    setPackResult(null);
    setClaimedOrder(null);
    setChecklistItems([]);
    setShowBagInput(false);
    setBagCount(1);
    setView("queue");
    fetchQueue();
  };

  // --- End session ---
  const handleEndSession = async () => {
    await signOut(auth);
    setStep("login");
    setVolunteerName("");
    setSessionCode("");
    setCycleInfo(null);
    setQueue(null);
    setClaimedOrder(null);
    setView("queue");
    setToast(null);
  };

  // ── LOGIN ─────────────────────────────────────────────────────────────────
  if (step === "login") {
    return (
      <div style={S.loginContainer}>
        <div style={S.loginCard}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              backgroundColor: LIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke={NAVY}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="1" y="3" width="15" height="13" rx="1" />
              <path d="M16 8h4l3 3v5h-7V8z" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: NAVY,
              margin: "0 0 6px",
            }}
          >
            Pack Orders
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "#64748b",
              lineHeight: 1.6,
              margin: "0 0 22px",
            }}
          >
            Enter your name and the session code to start packing.
          </p>

          {errorMsg && (
            <p style={{ fontSize: 13, color: "#ef4444", marginBottom: 12 }}>
              {errorMsg}
            </p>
          )}

          <input
            style={S.input}
            type="text"
            placeholder="Your first name"
            value={volunteerName}
            onChange={(e) => setVolunteerName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleStart()}
            autoComplete="off"
          />
          <input
            style={{
              ...S.input,
              fontWeight: 600,
            }}
            type="tel"
            placeholder="4-digit pack code"
            value={sessionCode}
            onChange={(e) => setSessionCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleStart()}
            autoComplete="off"
            maxLength={4}
          />

          <button
            style={{
              ...S.btnPrimary,
              ...(!volunteerName.trim() || !sessionCode.trim() || loading
                ? S.btnDisabled
                : {}),
            }}
            onClick={handleStart}
            disabled={!volunteerName.trim() || !sessionCode.trim() || loading}
          >
            {loading ? "Starting..." : "Start Packing"}
          </button>

          <button
            onClick={() => navigate("/")}
            style={{
              display: "block",
              margin: "16px auto 0",
              background: "none",
              border: "none",
              color: MID,
              fontSize: 14,
              cursor: "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // ── PORTAL ─────────────────────────────────────────────────────────────────
  const summary = queue?.summary || {};
  const availableOrders = queue?.orders || [];
  const cycleDate = cycleInfo?.pickupDate
    ? formatCycleDate(cycleInfo.pickupDate)
    : cycleInfo?.cycleId
      ? formatCycleDate(cycleInfo.cycleId)
      : "";

  const checkedCount = checklistItems.filter((i) => i.checked).length;

  return (
    <div style={S.page}>
      {/* Sticky header */}
      <div style={S.header}>
        <div>
          <h1 style={S.headerTitle}>Pack Orders</h1>
          <p style={S.headerSub}>
            {volunteerName}
            {cycleDate ? ` — ${cycleDate}` : ""}
          </p>
        </div>
        <button style={S.endBtn} onClick={handleEndSession}>
          End Session
        </button>
      </div>

      {/* Toast banner */}
      {toast && (
        <div
          style={{
            backgroundColor: "#FEF3C7",
            borderBottom: "1px solid #FCD34D",
            padding: "12px 16px",
            fontSize: 13,
            color: "#92400E",
            fontWeight: 500,
          }}
        >
          {toast}
        </div>
      )}

      {/* Claimed order persistent banner (queue view only) */}
      {claimedOrder && view === "queue" && (
        <div
          style={{
            backgroundColor: "#DBEAFE",
            borderBottom: "1px solid #93C5FD",
            padding: "12px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1E40AF" }}>
            You have a claimed order
            {minutesRemaining !== null
              ? ` — ${minutesRemaining} minute${minutesRemaining !== 1 ? "s" : ""} remaining`
              : ""}
          </span>
          <button
            onClick={() => setView("checklist")}
            style={{
              flexShrink: 0,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 700,
              backgroundColor: "#1E40AF",
              color: "#FFF",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Continue Packing
          </button>
        </div>
      )}

      <div style={S.content}>
        {/* Summary bar */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 8,
            marginBottom: 16,
          }}
        >
          {[
            {
              label: "Available",
              count: summary.available ?? "—",
              color: "#16A34A",
              bg: "#DCFCE7",
            },
            {
              label: "In Progress",
              count: summary.inProgress ?? "—",
              color: "#2563EB",
              bg: "#DBEAFE",
            },
            {
              label: "Packed",
              count: summary.packed ?? "—",
              color: "#059669",
              bg: "#D1FAE5",
            },
            {
              label: "Flagged",
              count: summary.flagged ?? "—",
              color: "#D97706",
              bg: "#FEF3C7",
            },
          ].map(({ label, count, color, bg }) => (
            <div
              key={label}
              style={{
                backgroundColor: bg,
                borderRadius: 10,
                padding: "10px 6px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 700, color }}>
                {count}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color,
                  opacity: 0.85,
                  marginTop: 2,
                  lineHeight: 1.2,
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Checklist view */}
        {view === "checklist" && claimedOrder && (
          <div>
            {/* Header */}
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 30,
                  fontWeight: 700,
                  color: NAVY,
                  letterSpacing: 2,
                }}
              >
                {claimedOrder.tokenId}
              </div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                {checklistItems.length} item
                {checklistItems.length !== 1 ? "s" : ""}
                {minutesRemaining !== null
                  ? ` — ${minutesRemaining} min remaining`
                  : ""}
              </div>
            </div>

            {checklistLoading ? (
              <p style={{ fontSize: 14, color: "#64748b", padding: "20px 0" }}>
                Loading order items...
              </p>
            ) : (
              checklistItems.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: item.flagged ? "#FFFBEB" : "#FFF",
                    borderRadius: 12,
                    marginBottom: 8,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    borderLeft: item.checked
                      ? "4px solid #16A34A"
                      : item.flagged
                        ? "4px solid #D97706"
                        : "4px solid transparent",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "14px 12px",
                      gap: 12,
                    }}
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleItemChecked(idx)}
                      style={{
                        flexShrink: 0,
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        border: item.checked ? "none" : "2px solid #CBD5E1",
                        backgroundColor: item.checked ? "#16A34A" : "#FFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      {item.checked && (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#FFF"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>

                    {/* Item info */}
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 36,
                        padding: "2px 8px",
                        borderRadius: 8,
                        background: item.checked ? "#94a3b8" : NAVY,
                        color: "#fff",
                        fontSize: 18,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {item.qty}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 16,
                        fontWeight: 600,
                        color: item.checked ? "#94a3b8" : NAVY,
                        textDecoration: item.checked ? "line-through" : "none",
                      }}
                    >
                      {item.name}
                    </div>

                    {/* Flag toggle */}
                    <button
                      onClick={() => toggleItemFlag(idx)}
                      style={{
                        flexShrink: 0,
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                        borderRadius: 6,
                        cursor: "pointer",
                        border: "1px solid",
                        backgroundColor: item.flagged
                          ? "#D97706"
                          : "transparent",
                        borderColor: item.flagged ? "#D97706" : "#CBD5E1",
                        color: item.flagged ? "#FFF" : "#64748b",
                      }}
                    >
                      Flag
                    </button>
                  </div>

                  {/* Flag detail section */}
                  {item.flagged && (
                    <div style={{ padding: "0 12px 14px 60px" }}>
                      <select
                        value={item.flagReason}
                        onChange={(e) =>
                          setChecklistItems((prev) =>
                            prev.map((it, i) =>
                              i === idx
                                ? { ...it, flagReason: e.target.value }
                                : it,
                            ),
                          )
                        }
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          fontSize: 13,
                          border: "1px solid #FCD34D",
                          borderRadius: 8,
                          marginBottom: 6,
                          backgroundColor: "#FFF",
                          boxSizing: "border-box",
                        }}
                      >
                        <option value="">Select reason...</option>
                        <option value="missing_from_shelf">
                          Missing from shelf
                        </option>
                        <option value="damaged">Damaged</option>
                        <option value="wrong_quantity">Wrong quantity</option>
                        <option value="other">Other</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Notes (optional)"
                        value={item.flagNotes}
                        onChange={(e) =>
                          setChecklistItems((prev) =>
                            prev.map((it, i) =>
                              i === idx
                                ? { ...it, flagNotes: e.target.value }
                                : it,
                            ),
                          )
                        }
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          fontSize: 13,
                          border: "1px solid #FCD34D",
                          borderRadius: 8,
                          backgroundColor: "#FFF",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Sticky bottom action bar */}
            <div
              style={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "#FFF",
                borderTop: "1px solid #E2E8F0",
                padding: "12px 16px 20px",
                boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
                zIndex: 40,
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  fontSize: 12,
                  color: "#64748b",
                  marginBottom: 8,
                }}
              >
                {checkedCount} of {checklistItems.length} items checked
              </div>

              <button
                onClick={handleConfirmPack}
                disabled={packSubmitting || checkedCount === 0}
                style={{
                  width: "100%",
                  padding: "14px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#FFF",
                  backgroundColor: checkedCount > 0 ? "#16A34A" : "#94a3b8",
                  border: "none",
                  borderRadius: 10,
                  cursor: checkedCount > 0 ? "pointer" : "not-allowed",
                  marginBottom: 8,
                  minHeight: 48,
                }}
              >
                {packSubmitting ? "Confirming..." : "Confirm Packed"}
              </button>

              {checkedCount === 0 && (
                <button
                  onClick={handleFlagOrder}
                  disabled={packSubmitting}
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#92400E",
                    backgroundColor: "#FEF3C7",
                    border: "1px solid #FCD34D",
                    borderRadius: 10,
                    cursor: "pointer",
                    marginBottom: 8,
                    minHeight: 44,
                  }}
                >
                  Flag Entire Order
                </button>
              )}

              <button
                onClick={handleRelease}
                disabled={packSubmitting}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "8px",
                  fontSize: 13,
                  color: "#DC2626",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Release Order
              </button>
            </div>
          </div>
        )}

        {/* Result view */}
        {view === "result" && claimedOrder && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            {packResult === "packed" ? (
              <>
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    backgroundColor: "#DCFCE7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#16A34A"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: NAVY,
                    margin: "0 0 8px",
                  }}
                >
                  Order Packed!
                </h2>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: MID,
                    letterSpacing: 2,
                    marginBottom: 12,
                  }}
                >
                  {claimedOrder.tokenId}
                </div>
                <p
                  style={{
                    fontSize: 14,
                    color: "#64748b",
                    lineHeight: 1.6,
                    marginBottom: 28,
                  }}
                >
                  Place the bag in the Ready-for-Pickup zone.
                </p>
                {showBagInput ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>Bags:</span>
                    <button
                      style={{ width: 36, height: 36, borderRadius: 8, border: '1.5px solid #cbd5e1', background: '#FFF', fontSize: 20, fontWeight: 700, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: NAVY }}
                      onClick={() => setBagCount(Math.max(1, (parseInt(bagCount) || 1) - 1))}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="10"
                      value={bagCount}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') { setBagCount(''); return; }
                        const n = parseInt(v, 10);
                        if (!isNaN(n)) setBagCount(Math.max(1, Math.min(10, n)));
                      }}
                      onBlur={() => { if (bagCount === '' || bagCount < 1) setBagCount(1); }}
                      style={{
                        width: 56,
                        padding: '4px 8px',
                        border: '1px solid #cbd5e1',
                        borderRadius: 6,
                        fontSize: 14,
                        textAlign: 'center',
                      }}
                    />
                    <button
                      style={{ width: 36, height: 36, borderRadius: 8, border: '1.5px solid #cbd5e1', background: '#FFF', fontSize: 20, fontWeight: 700, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: NAVY }}
                      onClick={() => setBagCount(Math.min(10, (parseInt(bagCount) || 1) + 1))}
                    >
                      +
                    </button>
                    <button
                      style={{ ...S.btnPrimary, padding: '4px 14px', fontSize: 13, width: 'auto' }}
                      onClick={() => printBagLabel(parseInt(bagCount) || 1)}
                    >
                      Print
                    </button>
                    <button
                      style={{ ...S.btnPrimary, backgroundColor: '#94a3b8', padding: '4px 10px', fontSize: 13, width: 'auto' }}
                      onClick={() => { setShowBagInput(false); setBagCount(1); }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    style={{ ...S.btnPrimary, backgroundColor: '#1A3B6E', marginBottom: 12 }}
                    onClick={() => setShowBagInput(true)}
                  >
                    Print Bag Label
                  </button>
                )}
                <button style={S.btnPrimary} onClick={handlePackComplete}>
                  Pack Next Order
                </button>
              </>
            ) : (
              <>
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    backgroundColor: "#FEF3C7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                  }}
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#D97706"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <h2
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: NAVY,
                    margin: "0 0 8px",
                  }}
                >
                  Order Flagged
                </h2>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: MID,
                    letterSpacing: 2,
                    marginBottom: 12,
                  }}
                >
                  {claimedOrder.tokenId}
                </div>
                <p
                  style={{
                    fontSize: 14,
                    color: "#64748b",
                    lineHeight: 1.6,
                    marginBottom: 28,
                  }}
                >
                  This order has been flagged for the Ops Lead to review.
                </p>
                <button style={S.btnPrimary} onClick={handlePackComplete}>
                  Back to Queue
                </button>
              </>
            )}
          </div>
        )}

        {/* Order queue */}
        {view === "queue" && (
          <>
            {availableOrders.length === 0 ? (
              <div
                style={{ ...S.card, textAlign: "center", padding: "40px 20px" }}
              >
                <div style={{ fontSize: 36, marginBottom: 12 }}>🎉</div>
                <p
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: NAVY,
                    margin: "0 0 8px",
                  }}
                >
                  All caught up! No orders waiting to be packed.
                </p>
                <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                  {summary.packed || 0} packed · {summary.flagged || 0} flagged
                </p>
              </div>
            ) : (
              availableOrders.map((order) => {
                const oid = order.orderId || order.id;
                const itemCount = order.itemCount ?? order.items?.length ?? 0;
                return (
                  <div key={oid} style={S.card}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: NAVY,
                            letterSpacing: 1,
                          }}
                        >
                          {order.tokenId}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#94a3b8",
                            marginTop: 4,
                          }}
                        >
                          {itemCount} item{itemCount !== 1 ? "s" : ""}
                          {order.submittedAt
                            ? ` · ${relativeTime(order.submittedAt)}`
                            : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => handleClaim(oid)}
                        style={{
                          padding: "10px 16px",
                          fontSize: 13,
                          fontWeight: 700,
                          backgroundColor: NAVY,
                          color: "#FFF",
                          border: "none",
                          borderRadius: 8,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        Claim & Pack
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
}
